import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { RefreshCw, Scale, CheckCircle, XCircle, Clock, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toLocaleString()}`;
};

const PORTFOLIO_COLORS = {
  bedrock: 'bg-emerald-100 text-emerald-700',
  fortress: 'bg-sky-100 text-sky-700',
  horizon: 'bg-indigo-100 text-indigo-700',
  velocity: 'bg-amber-100 text-amber-700',
  summit: 'bg-orange-100 text-orange-700',
  nebula: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  executed: { bg: 'bg-sky-100', text: 'text-sky-700', icon: CheckCircle },
};

export default function RebalancingPage() {
  const [portfolios, setPortfolios] = useState([]);
  const [rebalancingActions, setRebalancingActions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [portRes, actionsRes] = await Promise.all([
        authFetch(`${API_BASE}/api/lms/portfolios`),
        authFetch(`${API_BASE}/api/lms/rebalancing`),
      ]);
      if (portRes.ok) setPortfolios(await portRes.json());
      if (actionsRes.ok) setRebalancingActions(await actionsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
    </div>
  );

  // Calculate drift for each portfolio
  const PORTFOLIO_TARGETS = {
    bedrock: 0.70,
    fortress: 0.60,
    horizon: 0.50,
    velocity: 0.40,
    summit: 0.35,
    nebula: 0.25,
  };
  
  const driftAnalysis = portfolios.map(p => {
    const liquidPct = p.metrics?.liquidBuffer ? p.metrics.liquidBuffer / p.totalAUM : 0;
    const targetLiquid = PORTFOLIO_TARGETS[p.portfolioType] || 0.5;
    const drift = (liquidPct - targetLiquid) * 100;
    
    return {
      portfolio: p.portfolioType,
      name: p.name,
      aum: p.totalAUM,
      currentLiquid: liquidPct,
      targetLiquid,
      drift,
      needsRebalance: Math.abs(drift) > 5,
    };
  });

  const needsRebalance = driftAnalysis.filter(d => d.needsRebalance).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Portfolio Rebalancing</h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor allocation drift and manage rebalancing actions
          </p>
        </div>
        <Button onClick={loadData} className="bg-indigo-600 hover:bg-indigo-700">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Portfolios Monitored</p>
            <p className="text-2xl font-bold text-gray-900">{portfolios.length}</p>
          </CardContent>
        </Card>
        <Card className={needsRebalance > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Need Rebalancing</p>
            <p className={`text-2xl font-bold ${needsRebalance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {needsRebalance}
            </p>
            <p className="text-xs text-gray-500">Drift &gt; 5%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Pending Actions</p>
            <p className="text-2xl font-bold text-amber-600">
              {rebalancingActions.filter(a => a.status === 'pending').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Executed This Month</p>
            <p className="text-2xl font-bold text-emerald-600">
              {rebalancingActions.filter(a => a.status === 'executed').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Drift Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Scale className="w-4 h-4 text-indigo-600" />
            Portfolio Allocation Drift
          </CardTitle>
          <CardDescription>Current vs target liquid allocation</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio</TableHead>
                <TableHead className="text-right">AUM</TableHead>
                <TableHead className="text-right">Current Liquid %</TableHead>
                <TableHead className="text-right">Target Liquid %</TableHead>
                <TableHead className="text-right">Drift</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {driftAnalysis.map((d) => (
                <TableRow key={d.portfolio}>
                  <TableCell>
                    <div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[d.portfolio] || 'bg-gray-100 text-gray-700'}`}>
                        {d.portfolio}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">{d.name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold">{fmt(d.aum)}</TableCell>
                  <TableCell className="text-right">{(d.currentLiquid * 100).toFixed(1)}%</TableCell>
                  <TableCell className="text-right">{(d.targetLiquid * 100).toFixed(1)}%</TableCell>
                  <TableCell className="text-right">
                    <span className={`flex items-center justify-end gap-1 font-medium ${
                      Math.abs(d.drift) > 5 ? (d.drift > 0 ? 'text-amber-600' : 'text-red-600') : 'text-gray-600'
                    }`}>
                      {d.drift > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {d.drift > 0 ? '+' : ''}{d.drift.toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={d.needsRebalance ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                      {d.needsRebalance ? 'Needs Review' : 'On Target'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {d.needsRebalance && (
                      <Button variant="outline" size="sm" className="h-7 text-xs">
                        <ArrowRight className="w-3 h-3 mr-1" /> Propose
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Rebalancing Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Rebalancing Actions</CardTitle>
          <CardDescription>History of rebalancing proposals and executions</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Portfolio</TableHead>
                <TableHead>Action</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Approved By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rebalancingActions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No rebalancing actions recorded. All portfolios are within target allocations.
                  </TableCell>
                </TableRow>
              ) : (
                rebalancingActions.slice(0, 10).map((action, i) => {
                  const statusStyle = STATUS_COLORS[action.status] || STATUS_COLORS.pending;
                  const StatusIcon = statusStyle.icon;
                  
                  return (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(action.date || Date.now()).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[action.portfolioType] || 'bg-gray-100 text-gray-700'}`}>
                          {action.portfolioType}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{action.action || 'Rebalance'}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(action.amount || 0)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {action.status || 'pending'}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{action.approvedBy || '-'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
